import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAssistance, fetchScenario } from "../api/assistance";
import { fetchTokenByValue } from "../api/token";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks";

export const AssistanceContext = React.createContext({});

/**
 * The assistance context provider
 * 
 * @param {Object} props The props of the component
 * @returns The assistance context provider
 */
const AssistanceProvider = ({ children }) => {
  const [assistanceStart, setAssistanceStart] = useState(null);
  const { fetchBearer } = useAuth();
  const [assistanceRequestToken, setAssistanceRequestToken] = useState("");

  /*
   * Fetching the assistance request ID from the input token.
   */

  /**
   * Wraps the token query function
   * 
   * @returns The token query function
   */
  const tokenQueryFunction = useCallback(
    async () => {
      const bearer = await fetchBearer();
      
      return fetchTokenByValue(assistanceRequestToken, bearer)
    },
    [assistanceRequestToken, fetchBearer],
  );

  const tokenQuery = useQuery({
    queryKey: ["token", assistanceRequestToken],
    queryFn: tokenQueryFunction,
    enabled: !!assistanceRequestToken,
  });

  /**
   * Handles the assistance request token change
   * 
   * @param {Object} event The event object
   */
  const onAssistanceRequestTokenChange = useCallback((event) => {
    setAssistanceRequestToken(event.target.value);
  }, []);

  const assistanceRequestId = useMemo(() => {
    if (tokenQuery.data?.Items.length > 0) {
      return tokenQuery.data.Items[0].request_id;
    }
    return null;
  }, [tokenQuery.data?.Items]);

  /*
   * Fetching the assistance from the assistance request ID.
   */

  /**
   * Wraps the assistance query function
   * 
   * @returns The assistance query function
   */
  const assistanceQueryFunction = useCallback(
    async () => {
      const bearer = await fetchBearer()

      return fetchAssistance(assistanceRequestId, bearer)
    },
    [assistanceRequestId, fetchBearer],
  );

  const assistanceQuery = useQuery({
    queryKey: ["assistance", assistanceRequestId],
    queryFn: assistanceQueryFunction,
    enabled: !!assistanceRequestId,
  });

  const assistance = useMemo(() => {
    return assistanceQuery.data?.Item;
  }, [assistanceQuery.data?.Item]);

  /**
   * Wraps the scenario query function
   * 
   * @returns The scenario query function
   */
  const scenarioQueryFunction = useCallback(
    async () => {
      const bearer = await fetchBearer()

      return fetchScenario(assistance?.scenario_id, bearer)
    },
    [assistance?.scenario_id, fetchBearer])

  const crashScenarioQuery = useQuery({
    queryKey: ["scenario"],
    queryFn: scenarioQueryFunction,
    enabled: assistance?.scenario_id?.length > 0,
  });

  const crashScenario = useMemo(
    () => crashScenarioQuery?.data,
    [crashScenarioQuery?.data]);

  const crashData = useMemo(() => crashScenario, [crashScenario]);
  const crashVideo = useMemo(() => crashScenario?.url_video, [crashScenario?.url_video]);
  const crashImpactTime = useMemo(() => crashScenario?.collision_time, [crashScenario?.collision_time]);

  /**
   * Handles the assistance leave
   * 
   * @param {Object} event The event object
   */
  const onAssistanceLeave = useCallback(() => {
    setAssistanceRequestToken("");
  }, []);

  useEffect(() => {
    if (assistance) {
      setAssistanceStart(Date.now());
    }
  }, [assistance]);

  const contextValue = useMemo(
    () => ({
      assistance,
      assistanceStart,
      assistanceRequestId,
      assistanceRequestToken,
      onAssistanceLeave,
      onAssistanceRequestTokenChange,
      crashData,
      crashVideo,
      crashImpactTime,
    }),
    [
      assistance,
      assistanceStart,
      assistanceRequestId,
      assistanceRequestToken,
      onAssistanceLeave,
      onAssistanceRequestTokenChange,
      crashData,
      crashVideo,
      crashImpactTime,
    ],
  );

  return (
    <AssistanceContext.Provider value={contextValue}>
      {children}
    </AssistanceContext.Provider>
  );
};

export default React.memo(AssistanceProvider);
